// Pure SMS-body preview helper for the admin SPA — a VERBATIM port of the
// dispatcher's composition logic from apps/rtb-api/src/domain/sms.ts
// (pickVariant + buildSmsBody). It is intentionally self-contained: admin-web
// CANNOT cross-import rtb-api (a project-reference / rootDir cross-import fails
// the build with TS6059), so the pure logic is duplicated here byte-for-byte.
//
// INVARIANT: this MUST stay identical to rtb-api/src/domain/sms.ts so the
// operator preview shows EXACTLY what the dispatcher will send — same
// MurmurHash3 fmix32 picker, same seed offsets (13/1, 29/7, 47/11), same
// {n}/{p} substitution, same footer fallback. Any change to buildSmsBody must
// be mirrored here (and is asserted by tests/smsPreview.test.ts).

/**
 * Defensive fallback for the opt-out suffix — mirrors rtb-api DEFAULT_FOOTER.
 * Used when the campaign / auto-send config has no footer (empty/undefined).
 */
export const DEFAULT_FOOTER = "Reply STOP to stop getting messages. Thank you.";

/** Default sample first name used by the preview ({n} substitution). */
export const SAMPLE_FIRST_NAME = "John";

/** The ordered template arrays the preview composes from. */
export interface SmsPreviewCopy {
  openers: string[]; // {n} → sample first name (used when a name is present)
  openers_no_name: string[]; // plain openers (no placeholder)
  actions: string[]; // plain actions (no placeholder)
  ctas: string[]; // {p} → callback_did
  callback_did: string; // e.g. "1-888-533-1148"
  footer: string; // operator-editable opt-out suffix (plain text)
}

/**
 * Deterministic, non-crypto variant picker — the MurmurHash3 fmix32 finalizer.
 * The same seed always yields the same index for a given len. VERBATIM port of
 * rtb-api/src/domain/sms.ts pickVariant.
 */
export function pickVariant(seed: number, len: number): number {
  let h = seed | 0;
  h = (h ^ (h >>> 16)) * 0x85ebca6b;
  h = (h ^ (h >>> 13)) * 0xc2b2ae35;
  h = h ^ (h >>> 16);
  return Math.abs(h) % len;
}

/**
 * Compose an SMS body deterministically from the supplied template arrays via
 * {n}/{p} string substitution. VERBATIM port of rtb-api/src/domain/sms.ts
 * buildSmsBody — same firstName sanitize (trim / slice(0,40) / strip
 * non-printable), same seed offsets, same footer fallback to DEFAULT_FOOTER.
 *
 * Default sample firstName is "John"; {p} resolves to `callback_did`.
 */
export function composeSmsPreview(input: {
  openers: string[];
  openers_no_name: string[];
  actions: string[];
  ctas: string[];
  callback_did: string;
  footer: string;
  firstName?: string;
  seed: number;
}): string {
  const { openers, openers_no_name, actions, ctas, callback_did, seed } = input;

  const firstName = (input.firstName ?? SAMPLE_FIRST_NAME)
    .trim()
    .slice(0, 40)
    .replace(/[^\x20-\x7E]/g, "");

  const openerSeed = seed * 13 + 1;
  const actionSeed = seed * 29 + 7;
  const ctaSeed = seed * 47 + 11;

  // MUST match rtb-api buildSmsBody exactly. Pick raw templates; substitute
  // {n}/{p} across the WHOLE body below so a placeholder in any field is filled.
  const opener =
    firstName.length > 0
      ? (openers[pickVariant(openerSeed, openers.length)] ?? "")
      : (openers_no_name[pickVariant(openerSeed, openers_no_name.length)] ?? "");

  const cta = ctas[pickVariant(ctaSeed, ctas.length)] ?? "";

  const footer =
    input.footer && input.footer.length > 0 ? input.footer : DEFAULT_FOOTER;

  // Period only if not already punctuated; a CTA that is empty or punctuation-
  // only contributes nothing (no stray " . "). MUST match rtb-api buildSmsBody.
  const ctaTrimmed = cta.trim();
  const ctaHasContent = /[A-Za-z0-9]/.test(ctaTrimmed);
  const ctaPart = !ctaHasContent
    ? ""
    : ` ${ctaTrimmed}${/[.!?]$/.test(ctaTrimmed) ? "" : "."}`;

  const action = actions[pickVariant(actionSeed, actions.length)] ?? "";

  // Substitute {n}/{p} across the ENTIRE composed body (opener + action + cta).
  const raw = `${opener} ${action}${ctaPart} ${footer}`;
  return raw.replace(/\{n\}/g, firstName).replace(/\{p\}/g, callback_did);
}
